import React from 'react';
import styles from './InviteUsersIcon.module.css';

// Contacts sit on one equal-radius circle around the hub (170,150).
const PEOPLE = [
  { x: 82, y: 62, g: 'personA' },
  { x: 258, y: 62, g: 'personB' },
  { x: 82, y: 238, g: 'personC' },
  { x: 258, y: 238, g: 'personD' },
];

// Each spoke: the visible connection line + the packet that travels along it.
const LINKS = [
  { d: `M 170 150 L ${PEOPLE[0].x} ${PEOPLE[0].y}`, dur: '5s', begin: '0s' },
  { d: `M 170 150 L ${PEOPLE[1].x} ${PEOPLE[1].y}`, dur: '5.6s', begin: '1.1s' },
  { d: `M 170 150 L ${PEOPLE[2].x} ${PEOPLE[2].y}`, dur: '5.2s', begin: '2s' },
  { d: `M 170 150 L ${PEOPLE[3].x} ${PEOPLE[3].y}`, dur: '6s', begin: '3s' },
];

/**
 * Animated 3D "share one link with everyone" illustration.
 * The whole scene floats and tilts in perspective; glossy spheres give depth,
 * and packets pulse out from the hub and back, mirroring this page's action:
 * share a single invite link with friends & family.
 */
export const InviteUsersIcon = () => (
  <div className={styles.iconContainer}>
    <div className={styles.scene}>
      <div className={styles.stage3d}>
        <svg
          width="340"
          height="300"
          viewBox="0 0 340 300"
          fill="none"
          overflow="visible"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.inviteIcon}
          role="img"
          aria-label="Share your invite link with friends and family"
        >
          {/* Connection lines with flowing dashes */}
          <g className={styles.connections}>
            {LINKS.map((l) => (
              <path
                key={l.d}
                className={styles.flowLine}
                d={l.d}
                stroke="url(#lineGradient)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="5 6"
              />
            ))}
          </g>

          {/* Glowing packets pulse from the hub out and back (depth via size) */}
          <g className={styles.packets}>
            {LINKS.map((l) => (
              <g key={`packet-${l.d}`}>
                <circle r="6" fill="url(#packetGlow)" opacity="0.7">
                  <animateMotion dur={l.dur} begin={l.begin} repeatCount="indefinite" path={l.d} keyPoints="0;1;0" keyTimes="0;0.5;1" calcMode="linear" />
                  <animate attributeName="r" values="3;8;3" dur={l.dur} begin={l.begin} repeatCount="indefinite" />
                </circle>
                <circle r="3" fill="#e0fbff">
                  <animateMotion dur={l.dur} begin={l.begin} repeatCount="indefinite" path={l.d} keyPoints="0;1;0" keyTimes="0;0.5;1" calcMode="linear" />
                  <animate attributeName="r" values="1.5;4;1.5" dur={l.dur} begin={l.begin} repeatCount="indefinite" />
                </circle>
              </g>
            ))}
          </g>

          {/* People nodes — glossy 3D spheres */}
          <g className={styles.people}>
            {PEOPLE.map((p) => (
              <g key={p.g} transform={`translate(${p.x}, ${p.y})`}>
                <ellipse cx="0" cy="30" rx="20" ry="5" fill="rgba(0,0,0,0.25)" />
                <circle cx="0" cy="0" r="26" fill="url(#nodeSphere)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
                <circle cx="0" cy="-6" r="8" fill={`url(#${p.g})`} />
                <path d="M -12 12 a 12 10 0 0 1 24 0" fill={`url(#${p.g})`} />
                <ellipse cx="-8" cy="-12" rx="8" ry="5" fill="url(#gloss)" opacity="0.55" />
              </g>
            ))}
          </g>

          {/* Central share / link hub — glossy 3D orb */}
          <g className={styles.hub}>
            <circle className={styles.hubRing} cx="170" cy="150" r="46" fill="none" stroke="url(#hubGradient)" strokeWidth="2" opacity="0.5" />
            <circle cx="170" cy="150" r="52" fill="url(#hubGlow)" opacity="0.4" />
            <ellipse cx="170" cy="196" rx="34" ry="8" fill="rgba(0,0,0,0.3)" />
            <circle cx="170" cy="150" r="40" fill="url(#hubSphere)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
            {/* Link / chain icon */}
            <g stroke="#ffffff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" fill="none">
              <path d="M 162 150 a 9 9 0 0 1 9 -9 l 8 0 a 9 9 0 0 1 0 18 l -3 0" />
              <path d="M 178 150 a 9 9 0 0 1 -9 9 l -8 0 a 9 9 0 0 1 0 -18 l 3 0" />
            </g>
            <ellipse cx="154" cy="132" rx="16" ry="10" fill="url(#gloss)" opacity="0.6" />
          </g>

          {/* Decorative sparkles */}
          <g className={styles.decorative} opacity="0.5">
            <circle cx="150" cy="55" r="3" fill="#22d3ee" />
            <circle cx="300" cy="150" r="2.5" fill="#3b82f6" />
            <circle cx="40" cy="150" r="2.5" fill="#10b981" />
            <circle cx="190" cy="255" r="3" fill="#8b5cf6" />
          </g>

          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22d3ee" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
            <radialGradient id="packetGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#67e8f9" />
              <stop offset="100%" stopColor="rgba(103,232,249,0)" />
            </radialGradient>
            <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#22d3ee" />
              <stop offset="100%" stopColor="rgba(34,211,238,0)" />
            </radialGradient>
            {/* Glossy highlight used across all spheres */}
            <radialGradient id="gloss" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
            {/* Hub as a shaded 3D orb: light top-left, dark bottom-right */}
            <radialGradient id="hubSphere" cx="35%" cy="30%" r="80%">
              <stop offset="0%" stopColor="#5eead4" />
              <stop offset="35%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#1e3a8a" />
            </radialGradient>
            {/* Person node base shaded to look spherical */}
            <radialGradient id="nodeSphere" cx="35%" cy="28%" r="85%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
              <stop offset="100%" stopColor="rgba(15,23,42,0.55)" />
            </radialGradient>
            <linearGradient id="personA" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f472b6" />
              <stop offset="100%" stopColor="#be185d" />
            </linearGradient>
            <linearGradient id="personB" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#818cf8" />
              <stop offset="100%" stopColor="#4f46e5" />
            </linearGradient>
            <linearGradient id="personC" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <linearGradient id="personD" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  </div>
);
